import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Menu, X, Ticket, Zap } from "lucide-react";
import { api } from "@/lib/api";

const links = [
  { label: "The Vibe", href: "#vibe" },
  { label: "About Jay", href: "#about" },
  { label: "Events", href: "#events" },
  { label: "Sections", href: "#sections" },
  { label: "Request a Song", href: "#request" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [ticketsMode, setTicketsMode] = useState("public");
  const [price, setPrice] = useState(10);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    api.get("/site/status").then((r) => {
      setTicketsMode(r.data.tickets_mode || "off");
      const amt = r.data.prices?.oct4;
      if (amt) setPrice(amt / 100);
    }).catch(() => {});
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const salesOff = ticketsMode === "off";
  const cta = salesOff
    ? { label: "Get Early Access", Icon: Zap }
    : { label: `Buy Ticket · $${price}`, Icon: Ticket };

  return (
    <motion.header
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10 py-3" : "py-6"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between">
        <a href="#top" className="flex items-center group" data-testid="nav-logo">
          <img src="/logo-jayatk.png" alt="Jay Atk" className="h-7 w-auto transition-transform group-hover:-translate-y-0.5" />
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a key={l.href} href={l.href} data-testid={`nav-link-${l.href.slice(1)}`}
              className="text-xs uppercase tracking-[0.22em] text-[#A19D94] hover:text-[#D4AF37] transition-colors duration-300">
              {l.label}
            </a>
          ))}
        </nav>

        <a href="#events" data-testid="nav-cta"
          className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] border border-[#D4AF37] bg-[#D4AF37] text-black px-6 py-3 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300">
          <cta.Icon className="w-4 h-4" /> {cta.label}
        </a>

        <button className="md:hidden text-[#D4AF37]" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black/95 border-t border-white/10 mt-3">
            <div className="flex flex-col px-6 py-6 gap-5">
              {links.map((l) => (
                <a key={l.href} href={l.href} onClick={() => setOpen(false)}
                  className="text-sm uppercase tracking-[0.2em] text-[#A19D94] hover:text-[#D4AF37]">
                  {l.label}
                </a>
              ))}
              <a href="#events" onClick={() => setOpen(false)} data-testid="nav-cta-mobile"
                className="inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] border border-[#D4AF37] bg-[#D4AF37] text-black px-6 py-4 mt-1 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300">
                <cta.Icon className="w-4 h-4" /> {cta.label}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
